// ═══════════════════════════════════════════════════════════════
// RETENTION REPORT — Donor Retention Analysis (v3)
// Path: src/components/reports/RetentionReport.jsx
//
// Three sections:
//   1. Split Retention — First-time vs Repeat, with YoY trend
//   2. Cohort Heatmap — Acquisition year → retention at each milestone
//   3. Interpretive help throughout via popup tooltips
//
// View mode toggle:
//   Pace (YTD)        — apples-to-apples through today's date in each FY
//                       (matches Fundraising Report Card and similar tools)
//   Year-over-Year    — full prior FY vs YTD current FY (legacy behavior)
//
// Fiscal year: July 1 – June 30 (PCPA)
// ═══════════════════════════════════════════════════════════════

import React, { useState, useMemo } from "react";
import {
  UserPlus,
  UserMinus,
  RefreshCw,
  Printer,
  HelpCircle,
  X,
  Users,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { ActionButton } from "../ui/ActionButton";

// ── Fiscal year helpers ───────────────────────────────────────
const FY_START_MONTH = 6; // July (zero-indexed)

const getCurrentFyStart = () => {
  const now = new Date();
  return now.getMonth() >= FY_START_MONTH ? now.getFullYear() : now.getFullYear() - 1;
};

const fyLabel = (yr) => `FY${String(yr).slice(2)}–${String(yr + 1).slice(2)}`;

const toFyStart = (giftDate) => {
  const d = new Date(giftDate + "T12:00:00");
  return d.getMonth() >= FY_START_MONTH ? d.getFullYear() : d.getFullYear() - 1;
};

const fmtPct = (val) => (val != null ? `${val}%` : "—");

// Same month/day as today, applied to the calendar year that contains the
// "today" portion of the given fiscal year. Used to truncate prior fiscal
// years to the comparable elapsed window for Pace retention.
const cutoffDateForFy = (fy, today) => {
  const calendarYear = today.getMonth() >= FY_START_MONTH ? fy : fy + 1;
  return new Date(calendarYear, today.getMonth(), today.getDate(), 12, 0, 0);
};

// ── Help Tooltip ──────────────────────────────────────────────
const HelpTip = ({ text, palette }) => {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
      <button
        onClick={() => setOpen((p) => !p)}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 1,
          display: "flex",
          alignItems: "center",
          opacity: open ? 1 : 0.4,
          transition: "opacity 0.15s",
        }}
      >
        <HelpCircle size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
      </button>
      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 999 }}
          />
          <div
            style={{
              position: "absolute",
              bottom: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              marginBottom: 6,
              width: 280,
              padding: "10px 14px",
              borderRadius: 8,
              background: palette.cardBg,
              border: `1px solid ${palette.cardBorder}`,
              boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
              fontSize: "0.6rem",
              lineHeight: 1.6,
              color: palette.text2,
              fontFamily: FONT_FAMILY,
              zIndex: 1000,
            }}
          >
            {text}
          </div>
        </>
      )}
    </span>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

export const RetentionReport = () => {
  const { palette, allGifts } = useApp();
  const loading = allGifts.length === 0;

  // Default to Pace — it's the apples-to-apples view that matches
  // industry tools and is the right number for board reporting.
  const [mode, setMode] = useState("pace");

  const currentFy = getCurrentFyStart();
  const fyYears = [];
  for (let yr = currentFy; yr >= currentFy - 5; yr--) fyYears.push(yr);

  // Build full-year and pace donor sets in a single pass over allGifts.
  const giftsByMemberFy = useMemo(() => {
    if (allGifts.length === 0) return null;
    const today = new Date();
    const memberFys = {};
    const fyMembers = {};
    const fyMembersPace = {};

    allGifts.forEach((gift) => {
      if (!gift.gift_date || !gift.member_id) return;
      const giftDate = new Date(gift.gift_date + "T12:00:00");
      const fy = giftDate.getMonth() >= FY_START_MONTH
        ? giftDate.getFullYear()
        : giftDate.getFullYear() - 1;

      if (!memberFys[gift.member_id]) memberFys[gift.member_id] = new Set();
      memberFys[gift.member_id].add(fy);

      if (!fyMembers[fy]) fyMembers[fy] = new Set();
      fyMembers[fy].add(gift.member_id);

      if (giftDate <= cutoffDateForFy(fy, today)) {
        if (!fyMembersPace[fy]) fyMembersPace[fy] = new Set();
        fyMembersPace[fy].add(gift.member_id);
      }
    });

    return { memberFys, fyMembers, fyMembersPace };
  }, [allGifts]);

  // ── Compute metrics for one year using a chosen donor-set source ──
  const computeYearMetrics = (yr, fySet, memberFys) => {
    const currentSet = fySet[yr] || new Set();
    const priorSet = fySet[yr - 1] || new Set();

    let firstTimePrior = 0;
    let firstTimeRetained = 0;
    let repeatPrior = 0;
    let repeatRetained = 0;

    priorSet.forEach((memberId) => {
      const theirFys = memberFys[memberId];
      // First-time / repeat distinction always uses full multi-year history,
      // since "is yr-1 their first FY ever?" is a year-level question that
      // shouldn't change based on within-year date cutoffs.
      let gaveBeforePrior = false;
      theirFys.forEach((fy) => {
        if (fy < yr - 1) gaveBeforePrior = true;
      });

      if (gaveBeforePrior) {
        repeatPrior++;
        if (currentSet.has(memberId)) repeatRetained++;
      } else {
        firstTimePrior++;
        if (currentSet.has(memberId)) firstTimeRetained++;
      }
    });

    let newDonors = 0;
    currentSet.forEach((memberId) => {
      if (!priorSet.has(memberId)) newDonors++;
    });

    const overallRetained = firstTimeRetained + repeatRetained;
    const overallRate = priorSet.size > 0 ? Math.round((overallRetained / priorSet.size) * 100) : null;
    const firstTimeRate = firstTimePrior > 0 ? Math.round((firstTimeRetained / firstTimePrior) * 100) : null;
    const repeatRate = repeatPrior > 0 ? Math.round((repeatRetained / repeatPrior) * 100) : null;

    return {
      totalDonors: currentSet.size,
      priorDonors: priorSet.size,
      overallRetained,
      overallRate,
      firstTimePrior,
      firstTimeRetained,
      firstTimeRate,
      repeatPrior,
      repeatRetained,
      repeatRate,
      lapsed: priorSet.size - overallRetained,
      newDonors,
    };
  };

  // ── Compute split retention metrics for both modes ─────────
  const splitMetrics = useMemo(() => {
    if (!giftsByMemberFy) return [];
    const { memberFys, fyMembers, fyMembersPace } = giftsByMemberFy;

    return fyYears.map((yr) => ({
      year: yr,
      label: fyLabel(yr),
      yoy: computeYearMetrics(yr, fyMembers, memberFys),
      pace: computeYearMetrics(yr, fyMembersPace, memberFys),
    }));
  }, [giftsByMemberFy, fyYears]);

  // ── Compute cohort heatmap (always full-year) ───────────────
  // Cohorts are a longitudinal view; mixing pace truncation into historic
  // cohorts adds complexity that doesn't change the central insight.
  const cohortData = useMemo(() => {
    if (!giftsByMemberFy) return [];
    const { memberFys, fyMembers } = giftsByMemberFy;

    const cohortYears = [];
    for (let yr = currentFy - 5; yr <= currentFy; yr++) cohortYears.push(yr);

    return cohortYears.map((acqYear) => {
      const cohortMembers = [];
      (fyMembers[acqYear] || new Set()).forEach((memberId) => {
        const theirFys = memberFys[memberId];
        let earliest = Infinity;
        theirFys.forEach((fy) => { if (fy < earliest) earliest = fy; });
        if (earliest === acqYear) cohortMembers.push(memberId);
      });

      const cohortSize = cohortMembers.length;
      if (cohortSize === 0) return { acqYear, label: fyLabel(acqYear), cohortSize, milestones: {} };

      const milestones = {};
      for (let yr = acqYear; yr <= currentFy; yr++) {
        const fySet = fyMembers[yr] || new Set();
        let activeCount = 0;
        cohortMembers.forEach((id) => { if (fySet.has(id)) activeCount++; });
        milestones[yr] = {
          count: activeCount,
          pct: Math.round((activeCount / cohortSize) * 100),
        };
      }

      return { acqYear, label: fyLabel(acqYear), cohortSize, milestones };
    });
  }, [giftsByMemberFy, currentFy]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 40, color: palette.text3, fontSize: FONT_SIZES.xs, fontFamily: FONT_FAMILY }}>
        Loading retention data...
      </div>
    );
  }

  const isPace = mode === "pace";
  const currentRow = splitMetrics[0];
  const current = currentRow ? (isPace ? currentRow.pace : currentRow.yoy) : null;

  // Today's month/day rendered nicely for the toggle label tooltip.
  const todayLabel = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" });

  // ── Color helper for rates ──────────────────────────────────
  const rateColor = (rate, benchmarkHigh = 50, benchmarkLow = 30) => {
    if (rate == null) return palette.text3;
    if (rate >= benchmarkHigh) return "#10b981";
    if (rate >= benchmarkLow) return "#f59e0b";
    return "#ef4444";
  };

  const heatColor = (pct) => {
    if (pct >= 70) return "#10b98140";
    if (pct >= 50) return "#10b98125";
    if (pct >= 30) return "#f59e0b20";
    if (pct >= 15) return "#ef444415";
    return "#ef444408";
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Header row: mode toggle + print */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              fontSize: "0.6rem",
              fontWeight: 700,
              color: palette.text3,
              letterSpacing: 0.4,
              fontFamily: FONT_FAMILY,
            }}
          >
            VIEW
          </span>
          <button
            onClick={() => setMode("pace")}
            title={`Compare each fiscal year through ${todayLabel} for an apples-to-apples view`}
            style={{
              fontSize: FONT_SIZES.xs,
              fontWeight: 700,
              padding: "4px 10px",
              borderRadius: 6,
              border: `1px solid ${isPace ? palette.accent1 : palette.cardBorder}`,
              background: isPace ? `${palette.accent1}18` : "transparent",
              color: isPace ? palette.accent1 : palette.text2,
              fontFamily: FONT_FAMILY,
              cursor: "pointer",
            }}
          >
            Pace (thru {todayLabel})
          </button>
          <button
            onClick={() => setMode("yoy")}
            title="Compare each fiscal year as a whole — useful for closed-year reporting"
            style={{
              fontSize: FONT_SIZES.xs,
              fontWeight: 700,
              padding: "4px 10px",
              borderRadius: 6,
              border: `1px solid ${!isPace ? palette.accent1 : palette.cardBorder}`,
              background: !isPace ? `${palette.accent1}18` : "transparent",
              color: !isPace ? palette.accent1 : palette.text2,
              fontFamily: FONT_FAMILY,
              cursor: "pointer",
            }}
          >
            Year-over-Year
          </button>
          <HelpTip
            palette={palette}
            text={
              isPace
                ? `Pace mode truncates every fiscal year to the same elapsed window — through ${todayLabel} of each FY. This is the apples-to-apples comparison: of donors who had given by this date last year, what share have given by this date this year. Industry tools like Fundraising Report Card use this method.`
                : `Year-over-Year mode compares the full prior fiscal year to the current FY's progress so far. Useful for "are we behind on retention this year?" but not directly comparable to industry benchmarks until the current FY closes.`
            }
          />
        </div>
        <ActionButton label="Print" icon={Printer} variant="secondary" small onClick={() => window.print()} />
      </div>

      {/* ═══ SECTION 1: KPI Tiles ══════════════════════════════ */}
      {current && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
          <KpiTile
            icon={UserPlus}
            label="First-Time Retention"
            value={fmtPct(current.firstTimeRate)}
            sub={`${current.firstTimeRetained} of ${current.firstTimePrior} new donors returned`}
            color={rateColor(current.firstTimeRate, 30, 15)}
            palette={palette}
            help={
              isPace
                ? `Of donors whose first-ever gift was last fiscal year and who gave by ${todayLabel} of that year, what percentage have given again by ${todayLabel} this year. Industry benchmark: ~25%. This is the hardest conversion — most first-time donors never give a second gift.`
                : `Of donors whose first-ever gift was last fiscal year, what percentage gave again this year. Industry benchmark: ~25%. Note: this compares a complete prior year to the current year-to-date.`
            }
          />
          <KpiTile
            icon={RefreshCw}
            label="Repeat Retention"
            value={fmtPct(current.repeatRate)}
            sub={`${current.repeatRetained} of ${current.repeatPrior} repeat donors continued`}
            color={rateColor(current.repeatRate, 60, 40)}
            palette={palette}
            help={
              isPace
                ? `Of donors who gave in at least two fiscal years (including last year by ${todayLabel}), what percentage gave again by ${todayLabel} this year. Industry benchmark: ~60%. This measures stewardship quality — are you keeping your loyal donors?`
                : `Of donors who gave in at least two fiscal years (including last year), what percentage gave again this year. Industry benchmark: ~60%. Note: this compares a complete prior year to the current year-to-date.`
            }
          />
          <KpiTile
            icon={UserMinus}
            label="Lapsed"
            value={current.lapsed}
            sub={`${current.priorDonors > 0 ? Math.round((current.lapsed / current.priorDonors) * 100) : 0}% of prior-year donors`}
            color="#ef4444"
            palette={palette}
            help={
              isPace
                ? `Donors who gave by ${todayLabel} last fiscal year but have not yet given by ${todayLabel} this year. Some may still give before year-end.`
                : `Donors who gave last fiscal year but have not yet given this year. These are the people your Pollen Fading card is tracking. Some may still give before year-end.`
            }
          />
          <KpiTile
            icon={Users}
            label="Overall"
            value={fmtPct(current.overallRate)}
            sub={`${current.overallRetained} of ${current.priorDonors} retained`}
            color={rateColor(current.overallRate)}
            palette={palette}
            help={
              isPace
                ? `Blended retention rate. In Pace mode, this is the apples-to-apples figure to compare against Fundraising Report Card and similar industry tools.`
                : `Blended retention rate across all donors. Useful for benchmarking but less actionable than the split view above.`
            }
          />
        </div>
      )}

      {/* ═══ SECTION 2: Split Retention Trend ══════════════════ */}
      <div
        style={{
          background: palette.cardBg,
          border: `1px solid ${palette.cardBorder}`,
          borderRadius: 10,
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${palette.cardBorder}`, display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
            Retention Trend — First-Time vs Repeat
            {isPace && (
              <span style={{ fontSize: "0.6rem", color: palette.text3, fontWeight: 500, marginLeft: 6 }}>
                (each FY truncated to {todayLabel})
              </span>
            )}
          </span>
          <HelpTip
            palette={palette}
            text="This table separates your donor base into two groups: first-time donors (gave for the first time last year) and repeat donors (have given in multiple years). Tracking these separately reveals whether your problem is acquisition follow-through or long-term stewardship."
          />
        </div>

        {/* Header */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.2fr repeat(7, 1fr)",
            gap: 4,
            padding: "10px 16px",
            borderBottom: `1px solid ${palette.cardBorder}`,
            fontSize: "0.55rem",
            fontWeight: 600,
            color: palette.text3,
            fontFamily: FONT_FAMILY,
          }}
        >
          <span>Fiscal Year</span>
          <span style={{ textAlign: "right" }}>{isPace ? "Donors (YTD)" : "Donors"}</span>
          <span style={{ textAlign: "right" }}>1st-Time Ret.</span>
          <span style={{ textAlign: "right" }}>Repeat Ret.</span>
          <span style={{ textAlign: "right" }}>Overall</span>
          <span style={{ textAlign: "right" }}>Lapsed</span>
          <span style={{ textAlign: "right" }}>New</span>
          <span style={{ textAlign: "right" }}>Net Change</span>
        </div>

        {splitMetrics.map((row, idx) => {
          const m = isPace ? row.pace : row.yoy;
          const netChange = m.totalDonors - m.priorDonors;
          return (
            <div
              key={row.year}
              style={{
                display: "grid",
                gridTemplateColumns: "1.2fr repeat(7, 1fr)",
                gap: 4,
                padding: "8px 16px",
                borderBottom: `1px solid ${palette.cardBorder}`,
                fontSize: FONT_SIZES.xs,
                fontFamily: FONT_FAMILY,
              }}
            >
              <span style={{ fontWeight: 700, color: palette.text1 }}>
                {row.label}
                {idx === 0 && !isPace && <span style={{ fontSize: "0.5rem", color: palette.text3, fontWeight: 400 }}> *</span>}
              </span>
              <span style={{ textAlign: "right", color: palette.text1 }}>{m.totalDonors}</span>
              <span style={{ textAlign: "right", fontWeight: 700, color: rateColor(m.firstTimeRate, 30, 15) }}>
                {fmtPct(m.firstTimeRate)}
              </span>
              <span style={{ textAlign: "right", fontWeight: 700, color: rateColor(m.repeatRate, 60, 40) }}>
                {fmtPct(m.repeatRate)}
              </span>
              <span style={{ textAlign: "right", fontWeight: 600, color: rateColor(m.overallRate) }}>
                {fmtPct(m.overallRate)}
              </span>
              <span style={{ textAlign: "right", color: "#ef4444" }}>{m.lapsed}</span>
              <span style={{ textAlign: "right", color: "#3b82f6" }}>{m.newDonors}</span>
              <span style={{ textAlign: "right", fontWeight: 600, color: netChange >= 0 ? "#10b981" : "#ef4444" }}>
                {netChange >= 0 ? "+" : ""}{netChange}
              </span>
            </div>
          );
        })}

        {!isPace && (
          <div style={{ padding: "8px 16px", fontSize: "0.5rem", color: palette.text3, fontFamily: FONT_FAMILY }}>
            * in progress — numbers will increase as more gifts are recorded this fiscal year
          </div>
        )}
      </div>

      {/* ═══ SECTION 3: Cohort Heatmap ═════════════════════════ */}
      <div
        style={{
          background: palette.cardBg,
          border: `1px solid ${palette.cardBorder}`,
          borderRadius: 10,
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${palette.cardBorder}`, display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
            Cohort Retention Heatmap
            <span style={{ fontSize: "0.6rem", color: palette.text3, fontWeight: 500, marginLeft: 6 }}>
              (full-year data, all modes)
            </span>
          </span>
          <HelpTip
            palette={palette}
            text="Each row is a 'cohort' — donors who made their first gift in that fiscal year. The columns show what percentage of that cohort gave in each subsequent year. Darker green = higher retention. Read left to right to see how each cohort decays over time. This view always uses full-year data so historic cohorts remain stable as the current year fills in."
          />
        </div>

        <div style={{ overflowX: "auto" }}>
          {/* Header row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `140px 60px repeat(${fyYears.length}, 1fr)`,
              gap: 2,
              padding: "8px 16px",
              borderBottom: `1px solid ${palette.cardBorder}`,
              fontSize: "0.55rem",
              fontWeight: 600,
              color: palette.text3,
              fontFamily: FONT_FAMILY,
              minWidth: 600,
            }}
          >
            <span>Acquired In</span>
            <span style={{ textAlign: "center" }}>Size</span>
            {fyYears.map((yr) => (
              <span key={yr} style={{ textAlign: "center" }}>{fyLabel(yr)}</span>
            ))}
          </div>

          {/* Cohort rows */}
          {cohortData.filter((c) => c.cohortSize > 0).map((cohort) => (
            <div
              key={cohort.acqYear}
              style={{
                display: "grid",
                gridTemplateColumns: `140px 60px repeat(${fyYears.length}, 1fr)`,
                gap: 2,
                padding: "6px 16px",
                borderBottom: `1px solid ${palette.cardBorder}`,
                fontSize: FONT_SIZES.xs,
                fontFamily: FONT_FAMILY,
                minWidth: 600,
              }}
            >
              <span style={{ fontWeight: 600, color: palette.text1 }}>
                {cohort.label}
              </span>
              <span style={{ textAlign: "center", color: palette.text2 }}>
                {cohort.cohortSize}
              </span>
              {fyYears.map((yr) => {
                const milestone = cohort.milestones[yr];
                if (!milestone || yr < cohort.acqYear) {
                  return <span key={yr} />;
                }
                const isAcqYear = yr === cohort.acqYear;
                return (
                  <div
                    key={yr}
                    style={{
                      textAlign: "center",
                      padding: "4px 2px",
                      borderRadius: 4,
                      background: isAcqYear ? `${palette.accent1 || "#3b82f6"}15` : heatColor(milestone.pct),
                      fontWeight: isAcqYear ? 700 : 600,
                      color: isAcqYear ? palette.accent1 || "#3b82f6" : palette.text1,
                      fontSize: "0.6rem",
                    }}
                    title={`${milestone.count} of ${cohort.cohortSize} donors (${milestone.pct}%)`}
                  >
                    {milestone.pct}%
                    <div style={{ fontSize: "0.45rem", color: palette.text3, fontWeight: 400 }}>
                      {milestone.count}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div style={{ padding: "10px 16px", display: "flex", gap: 12, fontSize: "0.5rem", color: palette.text3, fontFamily: FONT_FAMILY, borderTop: `1px solid ${palette.cardBorder}` }}>
          <span>Retention:</span>
          <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, background: "#10b98140" }} /> 70%+
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, background: "#10b98125" }} /> 50–69%
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, background: "#f59e0b20" }} /> 30–49%
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, background: "#ef444415" }} /> 15–29%
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, background: "#ef444408" }} /> &lt;15%
          </div>
        </div>
      </div>

      {/* ═══ SECTION 4: Interpretation Guide ═══════════════════ */}
      <div
        style={{
          background: palette.cardBg,
          border: `1px solid ${palette.cardBorder}`,
          borderRadius: 10,
          padding: "16px 20px",
        }}
      >
        <div style={{ fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY, marginBottom: 10 }}>
          How to Read This Report
        </div>
        <div style={{ fontSize: "0.65rem", lineHeight: 1.8, color: palette.text2, fontFamily: FONT_FAMILY }}>
          <p style={{ margin: "0 0 8px" }}>
            <strong style={{ color: palette.text1 }}>Pace vs Year-over-Year:</strong>{" "}
            Pace mode truncates every fiscal year to the same elapsed window — through today's date.
            That's the apples-to-apples view that matches industry tools like Fundraising Report Card,
            and the right view for board reporting and benchmarking.
            Year-over-Year mode compares the complete prior fiscal year to the current FY's progress so far —
            useful for "are we behind on retention this year?" but it understates retention until the current FY closes.
          </p>
          <p style={{ margin: "0 0 8px" }}>
            <strong style={{ color: palette.text1 }}>First-Time Retention (~25% benchmark):</strong>{" "}
            Of donors whose first gift was last fiscal year, what percentage gave again this year.
            This is the "second gift" problem — the single biggest leverage point in fundraising.
            Your Retention Danger Zone card tracks these donors for action.
          </p>
          <p style={{ margin: "0 0 8px" }}>
            <strong style={{ color: palette.text1 }}>Repeat Retention (~60% benchmark):</strong>{" "}
            Of donors who have given in multiple years, what percentage continued this year.
            This measures stewardship quality. If this drops, your donor relationships are eroding.
            Your Pollen Fading card tracks these lapsing donors.
          </p>
          <p style={{ margin: "0 0 8px" }}>
            <strong style={{ color: palette.text1 }}>Cohort Heatmap:</strong>{" "}
            Read each row left-to-right to see how a group of donors decays over time.
            The acquisition year cell is always 100% (that's when they first gave).
            Subsequent cells show what fraction returned. Compare rows: if newer cohorts
            show higher retention at the same milestones, your cultivation processes are improving.
            The cohort heatmap always uses full-year data so historic cohorts stay stable.
          </p>
          <p style={{ margin: 0 }}>
            <strong style={{ color: palette.text1 }}>Net Change:</strong>{" "}
            Are you gaining or losing donors overall? If new donors don't exceed lapsed donors,
            your donor base is shrinking — even if retention rates look okay.
          </p>
        </div>
      </div>
    </div>
  );
};

// ── KPI Tile with help ────────────────────────────────────────
const KpiTile = ({ icon: Icon, label, value, sub, color, palette, help }) => (
  <div
    style={{
      background: palette.cardBg,
      border: `1px solid ${palette.cardBorder}`,
      borderRadius: 10,
      padding: "14px 16px",
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
    }}
  >
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: 8,
        background: `${color}15`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon size={ICON_SIZES.md} style={{ color }} />
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <span style={{ fontSize: FONT_SIZES.md, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
          {value}
        </span>
        {help && <HelpTip palette={palette} text={help} />}
      </div>
      <div style={{ fontSize: "0.55rem", color: palette.text3, fontFamily: FONT_FAMILY }}>
        {label}
      </div>
      {sub && (
        <div style={{ fontSize: "0.5rem", color: palette.text3, fontFamily: FONT_FAMILY, marginTop: 2 }}>
          {sub}
        </div>
      )}
    </div>
  </div>
);
